import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { issuesApi } from "@/api/issues";
import { queryKeys } from "@/lib/query-keys";
import { ListChecks, Plus, ArrowRight } from "lucide-react";

const statusVariant: Record<string, "success" | "warning" | "danger" | "default" | "secondary"> = {
  backlog: "secondary",
  todo: "default",
  in_progress: "warning",
  in_review: "warning",
  blocked: "danger",
  done: "success",
  cancelled: "default",
};

const priorityVariant: Record<string, "danger" | "warning" | "default" | "secondary"> = {
  urgent: "danger",
  high: "danger",
  medium: "warning",
  low: "secondary",
};

export function Issues({
  onOpenIssue,
}: {
  onOpenIssue?: (issueId: string) => void;
} = {}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const { data: issues = [], isLoading } = useQuery({
    queryKey: queryKeys.issues.byCompany(tenantId),
    queryFn: () => issuesApi.list(tenantId),
  });
  const { data: agents = [] } = useAgentsByCompany(tenantId);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ title: "", priority: "medium", assigneeAgentId: "" });

  const createMutation = useMutation({
    mutationFn: () => issuesApi.create({
      companyId: tenantId,
      title: form.title,
      priority: form.priority,
      assigneeAgentId: form.assigneeAgentId || undefined,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.issues.byCompany(tenantId) });
      setShowCreate(false);
      setForm({ title: "", priority: "medium", assigneeAgentId: "" });
    },
  });

  return (
    <CenterPanel title="Issues">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-text-secondary">{issues.length} issue{issues.length !== 1 ? "s" : ""}</p>
        <Button size="sm" onClick={() => setShowCreate(!showCreate)}>
          <Plus size={16} className="mr-1.5" />
          New Issue
        </Button>
      </div>

      {showCreate && (
        <Card className="mb-4">
          <CardContent className="space-y-3 pt-4">
            <Input
              placeholder="Issue title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              onKeyDown={(e) => e.key === "Enter" && form.title.trim() && createMutation.mutate()}
            />
            <div className="flex gap-3">
              <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="rounded-md border border-border bg-surface px-3 py-2 text-sm">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
              <select value={form.assigneeAgentId} onChange={(e) => setForm({ ...form, assigneeAgentId: e.target.value })} className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm">
                <option value="">Unassigned</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div className="flex gap-2">
              <Button size="sm" disabled={!form.title.trim() || createMutation.isPending} onClick={() => createMutation.mutate()}>Create</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {isLoading ? (
        <p className="text-sm text-text-muted">Loading issues...</p>
      ) : issues.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <ListChecks size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">No issues yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-1.5">
          {issues.map((issue) => {
            const assignee = agents.find((a) => a.id === issue.assigneeAgentId);
            return (
              <Card
                key={issue.id}
                className="cursor-pointer transition-colors hover:border-accent/40"
                onClick={() => {
                  if (onOpenIssue) {
                    onOpenIssue(issue.id);
                    return;
                  }
                  navigate(`/tenants/${tenantId}/issues/${issue.id}`);
                }}
              >
                <CardContent className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {issue.identifier && (
                      <span className="shrink-0 text-xs font-mono text-text-muted">{issue.identifier}</span>
                    )}
                    <span className="truncate text-sm text-text-primary">{issue.title}</span>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {assignee && <span className="text-xs text-text-muted">{assignee.name}</span>}
                    <Badge variant={priorityVariant[issue.priority] ?? "default"} className="text-[10px]">
                      {issue.priority}
                    </Badge>
                    <Badge variant={statusVariant[issue.status] ?? "default"} className="text-[10px]">
                      {issue.status.replace("_", " ")}
                    </Badge>
                    <ArrowRight size={14} className="text-text-muted" />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </CenterPanel>
  );
}
